import { Component, Vue } from 'vue-facing-decorator';;
import { ElMessage, ElMessageBox } from 'element-plus';
import { Collection, Check, Delete, EditPen, CirclePlusFilled } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from '@/models/EnumNavigationIndex';
import ApiService from '@/services/ApiService';
import AuthService from '@/services/AuthService';

@Component(
{
    components:
    {
        Header,
        Navigation,
		Collection,
		Check,
		EditPen,
		Delete,
		CirclePlusFilled
    },
    enums:
    {
        NavigationIndex
    }
})

export default class Reference extends Vue 
{
    private activeName : string = '1';  
    private referenceModelList : ReferenceModel[] = [];
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    private loading : boolean = true;
    private formValid : boolean = false;
    private hasPermission: boolean[] = [] as boolean[];
    private toDelete : ReferenceModel | undefined;
    private newReferenceModel : ReferenceModel = {} as ReferenceModel;
	private currentReference : ReferenceModel | undefined;
    private filter : string = '';
    private page : number = 1;
    private rowsPerPage : number = 10;
    private displayList : ReferenceModel[] = [] as ReferenceModel[];
    private filteredList : ReferenceModel[] = [] as RefenceModel[];
    private rules =
    {
        name:
        [
            {
                required: true,
                message: 'Bitte geben Sie eine Bezeichnung ein.',
                trigger: 'blur'
            }
        ]
    };

    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['REFN_SELECT'] = AuthService.hasPermission('REFN_SELECT');
        this.hasPermission['REFN_INSERT'] = AuthService.hasPermission('REFN_INSERT');
        this.hasPermission['REFN_UPDATE'] = AuthService.hasPermission('REFN_UPDATE');
        this.hasPermission['REFN_DELETE'] = AuthService.hasPermission('REFN_DELETE');
        
        if (this.hasPermission['REFN_SELECT'])
        {
            await this.loadList();
        }
        
        if (this.referenceModelList)
        {
            await this.filterList();
            this.showPage();
        }

        window.addEventListener('resize', this.onWindowResize);
        window.scrollTo(0,0);

        this.loading = false;
    }

    private beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
    }

	private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private async loadList()
    {
        this.loading = true;
        this.referenceModelList = await ApiService.getAllReferences() as ReferenceModel[];
        this.loading = false;
    }

    private async filterList()
    {   
        // reset filtered list
        this.filteredList = [] as ReferenceModel[];
        
        // if a filter is set, add remaining records to filtered list
        if (this.filter.length > 0)
        {
            let i:number = 0;
            for (i = 0; i < this.referenceModelList.length; i++)
            {
                if (this.referenceModelList[i].title.toLowerCase().includes(this.filter.toLowerCase())
                   || this.referenceModelList[i].author.toLowerCase().includes(this.filter.toLowerCase())
                   || this.referenceModelList[i].issue.toLowerCase().includes(this.filter.toLowerCase())
                   || this.referenceModelList[i].isbn.toLowerCase().includes(this.filter.toLowerCase()))
                {
                    this.filteredList.push(this.referenceModelList[i]);
                }
            }
            this.page = 1;
        }
        else
        {
            this.filteredList = this.referenceModelList;
        }

        return this.filter;
    }

    private async showPage()
    {
        // reset display list
        this.displayList = [] as ReferenceModel[];

        // only add current page records to the display list
        let i:number = 0;
        if (this.referenceModelList && this.filteredList)
        {
            for (i = 0; i < this.filteredList.length; i++)
            {
                if (i >= (this.page-1)*this.rowsPerPage && i < this.page*this.rowsPerPage)
                {
                    this.displayList.push(this.filteredList![i]);
                }
            }
        }

        return this.page;
    }

    private onPageChange(p : number)
    {
        this.page = p;
        this.showPage();
    }

    private onFilterChange()
    {
        this.filterList();
        this.showPage();
    }

    private async createReference()
    {
        // convert release date to timestamp
        if (this.newReferenceModel.releasedate)
        {
            this.newReferenceModel.releasedate = ApiService.getTimestampForTimezone(this.newReferenceModel.releasedate, "Europe/Berlin") as any;
        }

        // send new reference model via request to API and await
        let reference = await ApiService.createReference(this.newReferenceModel);

        // check if successful
        if (reference)
        {
			ElMessage(
            {
               	message: "Neue Referenz erfolgreich erstellt.",
               	type: 'success',
            });

            this.activeName = "1";
            this.newReferenceModel = {} as ReferenceModel;
            this.formValid = false;
            await this.loadList();
            this.onFilterChange();
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Beim Erstellen der neuen Referenz ist ein Fehler aufgetreten.",
               	type: 'error',
            });
        }
    }

    private async updateReference(reference : ReferenceModel)
    {
        // abort, if no reference given
        if (!reference) return;
        
        // check date type, and fix if necessary
        if (typeof(reference.displaydate) == 'string') reference.displaydate = new Date(reference.displaydate);
        
        // convert selected date to timezone
        reference.releasedate = ApiService.getTimestampForTimezone(reference.displaydate, "Europe/Berlin") as any;

        let ok : any = await ApiService.updateReference(reference);

        if (ok)
        {
			ElMessage(
            {
               	message: "Änderungen wurden übernommen.",
               	type: 'success',
            });
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Änderungen konnten nicht gespeichert werden.",
               	type: 'error',
            });

        }
    }

    private async deleteReference(reference : ReferenceModel)
    {
        let ok = await ApiService.deleteReference(reference);

        if (ok)
        {
			ElMessage(
            {
               	message: "Referenz wurde gelöscht.",
               	type: 'success',
            });
            await this.loadList();
            this.onFilterChange();
        }
        else
        {
            // or show error message
			ElMessage(
            {
               	message: "Referenz konnte nicht gelöscht werden.",
               	type: 'error',
            });
        }
    }

    private validateForm() : boolean
    {
        if (!this.newReferenceModel.title || this.newReferenceModel.title.length <= 0) return false;

        return true;
    }

    private onInput()
    {
        this.formValid = this.validateForm();
    }

    private onReferenceSaveClick(reference : ReferenceModel)
    {
        this.updateReference(reference);
    }

    private onDeleteConfirm(action : string)
    {
        if (this.toDelete && action == 'confirm')
        {
            this.deleteReference(this.toDelete);
            this.loadList();
            this.onFilterChange();
        }
    }

    private onReferenceDeleteClick(reference : ReferenceModel)
    {
        this.toDelete = reference;

        ElMessageBox.confirm('Soll diese Referenz wirklich gelöscht werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onDeleteConfirm,
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });
    }

    private onNewReferenceClick()
    {
        this.createReference();
    }
}
